const APPLIED_DISCOUNT_COOKIE = 'applied-discount-code';
const APPLIED_DISCOUNT_COOKIE_DAYS = 7;
const TITLE_PLACEHOLDER = '__TITLE__';
const AMOUNT_PLACEHOLDER = '__AMOUNT__';

if (!customElements.get('discount-applied-bar')) {
  class DiscountAppliedBar extends HTMLElement {
    connectedCallback() {
      this.labelEl = this.querySelector('[data-discount-label]');
      this.defaultLabel = this.dataset.defaultLabel || '';
      this.titleTemplate = this.dataset.titleTemplate || '';
      this.amountTemplate = this.dataset.amountTemplate || '';

      this.captureDiscountFromUrl();
      if (this.hasStoredDiscount()) this.show();
      this.syncFromCart();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          this.syncFromCart();
        });
      }
    }

    disconnectedCallback() {
      if (typeof this.unsubscribeCart === 'function') {
        this.unsubscribeCart();
      }
    }

    captureDiscountFromUrl() {
      const code = new URLSearchParams(window.location.search).get('discount');
      if (!code || typeof setCookie !== 'function') return;
      setCookie(APPLIED_DISCOUNT_COOKIE, code, APPLIED_DISCOUNT_COOKIE_DAYS);
    }

    hasStoredDiscount() {
      if (new URLSearchParams(window.location.search).get('discount')) return true;
      if (typeof getCookie !== 'function') return false;
      return Boolean(getCookie(APPLIED_DISCOUNT_COOKIE));
    }

    cartHasDiscount(cart) {
      if (!cart) return false;
      if ((cart.cart_level_discount_applications || []).length > 0) return true;
      if ((cart.discount_codes || []).some((entry) => entry && entry.code)) return true;
      if (Number(cart.total_discount) > 0) return true;

      return (cart.items || []).some((item) => {
        if ((item.discounts || []).length > 0) return true;
        if ((item.line_level_discount_allocations || []).length > 0) return true;
        return false;
      });
    }

    getDiscountTitle(cart) {
      const cartLevel = cart?.cart_level_discount_applications || [];
      if (cartLevel[0]?.title) return cartLevel[0].title;

      for (const item of cart?.items || []) {
        if (item.discounts?.[0]?.title) return item.discounts[0].title;

        for (const allocation of item.line_level_discount_allocations || []) {
          const title = allocation.discount_application?.title;
          if (title) return title;
        }
      }

      return this.dataset.discountTitle || '';
    }

    getDiscountAmountCents(cart) {
      const total = Number(cart?.total_discount);
      if (total > 0) return total;

      let cents = 0;
      for (const application of cart?.cart_level_discount_applications || []) {
        cents += Number(application.total_allocated_amount) || 0;
      }

      for (const item of cart?.items || []) {
        for (const discount of item.discounts || []) {
          cents += Number(discount.amount) || 0;
        }
        for (const allocation of item.line_level_discount_allocations || []) {
          cents += Number(allocation.amount) || 0;
        }
      }

      return cents;
    }

    formatAmount(cents) {
      if (!cents) return '';

      const major = cents / 100;
      const moneyFormat = window.theme?.settings?.money_format;
      if (typeof formatPrice === 'function' && moneyFormat) {
        return formatPrice(moneyFormat, major).replace(/<[^>]+>/g, '').trim();
      }

      try {
        return new Intl.NumberFormat(window.Shopify?.locale || undefined, {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'EUR',
          minimumFractionDigits: Number.isInteger(major) ? 0 : 2,
          maximumFractionDigits: 2
        }).format(major);
      } catch (error) {
        return `${major}`;
      }
    }

    setLabel({ title, amount } = {}) {
      if (!this.labelEl) return;

      if (amount && this.amountTemplate) {
        this.labelEl.textContent = this.amountTemplate.split(AMOUNT_PLACEHOLDER).join(amount);
        return;
      }

      if (title && this.titleTemplate) {
        this.labelEl.textContent = this.titleTemplate.split(TITLE_PLACEHOLDER).join(title);
        return;
      }

      this.labelEl.textContent = this.defaultLabel;
    }

    refreshHeaderHeight() {
      if (typeof setRootCustomProperties === 'function') {
        setRootCustomProperties();
      }
    }

    show(cart) {
      const amount = cart
        ? this.formatAmount(this.getDiscountAmountCents(cart))
        : (this.dataset.discountAmount || '');

      this.setLabel({
        title: this.getDiscountTitle(cart),
        amount
      });
      this.classList.remove('hidden');
      this.refreshHeaderHeight();
    }

    hide() {
      this.classList.add('hidden');
      this.refreshHeaderHeight();
    }

    syncVisibility(cart) {
      if (this.cartHasDiscount(cart) || this.hasStoredDiscount()) {
        this.show(cart);
        return;
      }
      this.hide();
    }

    async syncFromCart() {
      try {
        const response = await fetch('/cart.js');
        if (!response.ok) return;
        const cart = await response.json();
        this.syncVisibility(cart);
      } catch (error) {
        // Keep Liquid / cookie visibility if cart cannot be fetched.
      }
    }
  }

  customElements.define('discount-applied-bar', DiscountAppliedBar);
}
